import { PromptAbortedError } from '../../errors'
import { resolveTemplateId, scaffoldFromTemplate } from '../../operations/init/create-service'
import type { Answers } from '../../templates/manifest'
import type { Template } from '../../templates/manifest'
import { DEFAULT_TEMPLATE_ALIAS, listTemplates } from '../../templates/registry'
import { cliCommand } from '../../utils/invocation'
import { fail } from '../fail'
import { offerDeploy } from '../offer-deploy'
import { say, sayError, section } from '../output'
import { createPrompter, selectTemplate } from '../prompts/ask'
import { reportCreateService } from '../reporter'

export interface CreateServiceCommandOptions {
  id?: string
  name?: string
  set?: string[]
  yes?: boolean
  list?: boolean
}

function printTemplates(templates: Template[]): void {
  const width = Math.max(...templates.map(({ manifest }) => manifest.id.length))

  section('Available templates:')
  say('')

  for (const { manifest, isDefault } of templates) {
    const marker = isDefault ? ' (default)' : ''

    say(`  ${ manifest.id.padEnd(width) }  ${ manifest.label }${ marker }`)
    say(`  ${ ' '.repeat(width) }  ${ manifest.description }`)
  }

  section(`Usage: ${ cliCommand() } create-service <template>`)
  say(`       ${ cliCommand() } cs ${ DEFAULT_TEMPLATE_ALIAS }   — shorthand for the default template`)
  say('')
}

/**
 * Prefilled answers, keyed by prompt name.
 *
 * `--set name=value` covers every prompt a template declares, so a new template is scriptable the day it
 * ships rather than when someone remembers to add a flag for it. `--id` / `--name` stay as shorthands for
 * the two every template is expected to ask.
 */
export function answersFrom(options: CreateServiceCommandOptions): Answers {
  const answers: Answers = {}

  for (const pair of options.set ?? []) {
    const separator = pair.indexOf('=')

    if (separator < 1) {
      throw new PromptAbortedError(`--set expects name=value, got "${ pair }"`)
    }

    answers[pair.slice(0, separator)] = pair.slice(separator + 1)
  }

  if (options.id) {
    answers.id = options.id
  }

  if (options.name) {
    answers.name = options.name
  }

  return answers
}

async function chooseTemplate(templateId: string | undefined, templates: Template[]): Promise<Template> {
  if (templateId) {
    return resolveTemplateId(templateId, templates)
  }

  // Asked even when only one template is bundled: `flowrunner cs` is documented as starting at the template
  // step, and skipping it would silently change behaviour the day a second template ships.
  if (!process.stdin.isTTY) {
    throw new PromptAbortedError('No template given and no interactive terminal. Pass a template name.')
  }

  return selectTemplate(templates)
}

export async function createServiceCommand(
  templateId: string | undefined,
  options: CreateServiceCommandOptions = {}
): Promise<void> {
  const templates = listTemplates()

  if (templates.length === 0) {
    sayError('No service templates are bundled with this CLI installation.')
    process.exit(1)
  }

  if (options.list) {
    printTemplates(templates)

    return
  }

  // Only offered when there is a terminal behind it; without one an unanswered prompt is an error, which is
  // what `PromptRequiredError` says and `fail` translates.
  const prompter = process.stdin.isTTY ? createPrompter() : null

  try {
    const template = await chooseTemplate(templateId, templates)
    const { relativePath, serviceId } = await scaffoldFromTemplate(template, {
      answers       : answersFrom(options),
      acceptDefaults: options.yes,
      ask           : prompter?.ask,
      onEvent       : reportCreateService,
    })

    prompter?.close()

    section(`  Edit ${ relativePath }/src/index.js to make it yours.`)

    if (await offerDeploy(serviceId)) {
      return
    }

    section('When you are ready:')
    say(`  ${ cliCommand() } deploy -s ${ serviceId }   — logs you in first if you have not already`)
    say('')
  } catch (error) {
    // Every failure here is a message meant for the user — an unknown template, a rejected id, an existing
    // service folder. A stack trace would bury it.
    fail(error)
  } finally {
    prompter?.close()
  }
}
